import { notification } from 'antd';
import { createAction } from 'redux-act';
import { paramsForServer } from 'feathers-hooks-common';
import { push } from 'connected-react-router';
import moment from 'moment';
import head from 'lodash/head';
//
import { KEYS, USER_TYPES_MAP } from 'utils/constants';
import { showNotification } from 'utils/appUtils';
import {
  BookingRequestService,
  BookingOptionService,
  OrganizationService,
  UserService,
  VocabService,
  LessonService,
  TopicService,
  OrganizationBookingService,
  SessionAssessmentResultService,
  WritingPadService,
} from 'services';
import { setLoading } from '../loading/actions';
import { getCurrentUser } from '../selectors';
import {
  fetchTopicSlides,
  fetchTopicAudioClips,
  fetchTopicQuestions,
  getTopic,
} from 'meeopp-redux/curriculumTopics/actions';
import { updatePresentation, updatePresentationUrl } from '../presentation/actions';
import { LESSON_TYPES } from '../../utils/constants';

export const toggleSupervisorUsers = createAction('toggleSupervisorUsers');

export const fetchOrganizationsSuccess = createAction('fetchOrganizationsSuccess');
export const fetchOrganizationSuccess = createAction('fetchOrganizationSuccess');
export const fetchOrganizationAdminsSuccess = createAction('fetchOrganizationAdminsSuccess');

export const clearVocabs = createAction('clearVocabs');
export const fetchVocabsSuccess = createAction('fetchVocabsSuccess');

export const fetchPastUserLessonsSuccess = createAction('fetchPastUserLessonsSuccess');

export const fetchOrgBookingLessonSuccess = createAction('fetchOrgBookingLessonSuccess');
export const fetchOrgBookingAlertSuccess = createAction('fetchOrgBookingAlertSuccess');
export const fetchStudentChallenges = createAction('fetchStudentChallenges');
export const fetchPreviewOrganizationDetailsSuccess = createAction(
  'fetchPreviewOrganizationDetailsSuccess'
);
export const fetchPreivewUserDetailsSuccess = createAction('fetchPreivewUserDetailsSuccess');
export const fetchRecentQuizScoresByTypeSuccess = createAction(
  'fetchRecentQuizScoresByTypeSuccess'
);
export const fetchLessonsOralScoresSuccess = createAction('fetchLessonsOralScoresSuccess');
export const clearOrgUser = createAction('clearOrgUser');
export const clearOrgTagsUsers = createAction('clearOrgTagsUsers');
export const getLastWritingPadSuccess = createAction('getLastWritingPadSuccess');
export const getOrganizationBookingOptions = createAction('getOrganizationBookingOptions');

export function fetchOrganizations() {
  return async (dispatch) => {
    dispatch(setLoading({ key: KEYS.fetchOrganizations, value: true }));
    try {
      const data = await OrganizationService.find({
        query: {
          $sort: '-createdAt',
        },
      });

      dispatch(fetchOrganizationsSuccess(data));
    } catch (err) {
      showNotification('error', 'Something went wrong!');
    } finally {
      dispatch(setLoading({ key: KEYS.fetchOrganizations, value: false }));
    }
  };
}

export function updateOrganization({ id, values, toggleModal }) {
  return async (dispatch) => {
    dispatch(
      setLoading({
        key: KEYS.submittingOrganization,
        value: true,
      })
    );

    try {
      await OrganizationService.patch(id, values);
      dispatch(fetchOrganizations());
      toggleModal && toggleModal();
    } catch (err) {
      notification.error({
        message: err.message,
      });
    } finally {
      dispatch(setLoading({ key: KEYS.submittingOrganization, value: false }));
    }
  };
}

export function fetchOrganization({ id }) {
  return async (dispatch) => {
    dispatch(
      setLoading({
        key: KEYS.fetchOrganization,
        value: true,
      })
    );

    try {
      const currentOrganization = await OrganizationService.get(id);
      dispatch(fetchOrganizationSuccess(currentOrganization));
      return currentOrganization;
    } catch (err) {
      notification.error({
        message: err.message,
      });
    } finally {
      dispatch(setLoading({ key: KEYS.fetchOrganization, value: false }));
    }
  };
}

export function fetchOrganizationAdmins({ organizationId }) {
  return async (dispatch) => {
    dispatch(
      setLoading({
        key: KEYS.fetchOrganization,
        value: true,
      })
    );

    try {
      const { data } = await UserService.find({
        query: {
          organizationId,
          userType: {
            $in: [USER_TYPES_MAP.schoolStaff, USER_TYPES_MAP.meeoppStaff],
          },
          $sort: { createdAt: '-1' },
        },
      });

      dispatch(fetchOrganizationAdminsSuccess(data));
    } catch (err) {
      showNotification('error', 'Something went wrong!');
    } finally {
      dispatch(setLoading({ key: KEYS.fetchOrganization, value: false }));
    }
  };
}

export function getUserVocabList({ userId }) {
  return async (dispatch) => {
    try {
      dispatch(
        setLoading({
          key: KEYS.fetchUserVocabs,
          value: true,
        })
      );

      const { data } = await VocabService.find({
        query: {
          customerId: userId,
          $sort: { createdAt: 1 },
        },
      });

      dispatch(fetchVocabsSuccess(data));
    } catch (e) {
      dispatch(clearVocabs());
    } finally {
      dispatch(
        setLoading({
          key: KEYS.fetchUserVocabs,
          value: false,
        })
      );
    }
  };
}

export function getLastWritingPad({ userId }) {
  return async (dispatch) => {
    try {
      dispatch(
        setLoading({
          key: KEYS.getLastWritingPad,
          value: true,
        })
      );

      const res = await LessonService.find({
        query: {
          status: { $ne: 'CANCELLED' },
          $or: [
            {
              customerId: userId,
              duration: { $gt: 0 },
              end: { $lt: new Date() },
            },
            {
              instructorId: userId,
              duration: { $gt: 0 },
              end: { $lte: new Date() },
            },
          ],
          $limit: 1,
          $sort: { start: -1 },
        },
      });

      const lessons = res.data;
      const lessonId = lessons[0]._id;
      const { data } = await WritingPadService.find({
        query: {
          lessonId,
        },
      });

      dispatch(getLastWritingPadSuccess(data[0]));
      dispatch(fetchPastUserLessonsSuccess(lessons));
    } catch (e) {
      showNotification('error', 'Something went wrong!');
    } finally {
      dispatch(
        setLoading({
          key: KEYS.getLastWritingPad,
          value: false,
        })
      );
    }
  };
}

// ==================== Organization Booking action ===== START =========
export function createOrgBookingLesson(values, options = { redirect: true }) {
  return async (dispatch, getState) => {
    dispatch(setLoading({ key: KEYS.fetchOrganizationBookingLesson, value: true }));

    let orgBookings = {};
    try {
      const user = getCurrentUser(getState());

      const { type, startAt, showAt, deadline, organizationAdminId } = values;

      const payload = {
        ...values,
        bookedBy: organizationAdminId || user._id,
        lessonType: type,
        inClassStartTime: startAt,
        soloShowAt: showAt,
        soloDeadline: deadline,
      };

      orgBookings = await OrganizationBookingService.create(payload);
      payload.orgBookingId = orgBookings._id;
      payload.schoolClassId = values.schoolClassIds[0];

      const result = payload.customerIds.map((id) => {
        let o = Object.assign({}, payload);
        o.customerId = id;
        o.start = o.startAt;
        delete o.startAt;
        return o;
      });

      await LessonService.create(
        result,
        paramsForServer({
          action: 'create-by-meeopp.net',
        })
      );

      showNotification('success', 'Sessions have been booked');
      if (options.redirect) dispatch(push('/organization/home'));
    } catch (err) {
      OrganizationBookingService.remove(orgBookings._id);
      showNotification('error', err.message);
    } finally {
      dispatch(
        setLoading({
          key: KEYS.fetchOrganizationBookingLesson,
          value: false,
        })
      );
    }
  };
}

export function createBookingRequest(values, options = { redirect: true }) {
  return async (dispatch) => {
    try {
      const { schoolClassIds } = values;
      await BookingRequestService.create(values);

      const description = 'We will contact you soon to confirm the schedule.';

      showNotification('success', 'Your request is successfully submitted!', {
        description,
        duration: 10,
      });

      if (options.redirect) {
        dispatch(push(`/organization/classes/activities/${head(schoolClassIds)}`));
      }
    } catch (error) {
      showNotification('error', error.message);
    }
  };
}

export function fetchOrgBookingLessonById(id) {
  return async (dispatch) => {
    dispatch(setLoading({ key: KEYS.fetchOrganizationBookingLesson, value: true }));

    try {
      const data = await OrganizationBookingService.get(
        id,
        paramsForServer({
          joins: {
            presentation: {
              slideAssets: true,
            },
            coreItemsIWGT: true,
            quiz: true,
            playbook: true,
            bookingRefMeta: true,
          },
          populate: 'TAG_STATISTICS',
        })
      );

      if (data.topicId) {
        dispatch(fetchTopicSlides(data.topicId));
        dispatch(fetchTopicAudioClips(data.topicId));
        dispatch(fetchTopicQuestions(data.topicId));
        dispatch(getTopic(data.topicId));
      }

      if (data.presentation) {
        dispatch(updatePresentationUrl(data.presentation.presentationUrl));
        dispatch(updatePresentation(data.presentation));
      }

      dispatch(fetchOrgBookingLessonSuccess(data));
    } catch (err) {
      showNotification('error', err.message);
    } finally {
      dispatch(
        setLoading({
          key: KEYS.fetchOrganizationBookingLesson,
          value: false,
        })
      );
    }
  };
}

export function updateOrgBookingLessons(orgBookingId, lesson) {
  return async (dispatch) => {
    const key = KEYS.fetchOrganizationBookingLesson;
    try {
      dispatch(setLoading({ key, value: true }));
      let submitValues = {};

      if (lesson) {
        if (lesson.start && lesson.duration) {
          submitValues = {
            ...submitValues,
            start: moment(lesson.start).format(),
            duration: parseInt(lesson.duration),
            end: moment(lesson.start).add(lesson.duration, 'minutes').startOf('minute').format(),
          };

          await OrganizationBookingService.patch(orgBookingId, {
            inClassStartTime: moment(lesson.start).format(),
          });
        } else if (lesson.showAt && lesson.deadline) {
          await OrganizationBookingService.patch(orgBookingId, {
            soloShowAt: moment(lesson.showAt).utc().format(),
            soloDeadline: moment(lesson.deadline).utc().format(),
          });

          submitValues = {
            ...submitValues,
            showAt: moment(lesson.showAt).format(),
            deadline: moment(lesson.deadline).format(),
          };
        }
      }

      await LessonService.patch(
        null,
        submitValues,
        paramsForServer({
          query: {
            orgBookingId,
            status: { $ne: 'CANCELLED' },
          },
          action: 'bulk-update',
        })
      );

      dispatch(fetchOrgBookingLessonById(orgBookingId));
    } catch (err) {
      dispatch(setLoading({ key, value: false }));
    }
  };
}

export function changeOrgBookingTopic(orgBookingId, newTopicId) {
  return async (dispatch) => {
    if (!orgBookingId || !newTopicId) return;
    const key = KEYS.fetchOrganizationBookingLesson;
    try {
      dispatch(setLoading({ key, value: true }));

      await OrganizationBookingService.patch(
        orgBookingId,
        {
          topicId: newTopicId,
        },
        paramsForServer({ action: 'change-topic' })
      );

      dispatch(fetchOrgBookingLessonById(orgBookingId));
    } catch (err) {
      if (orgBookingId && newTopicId) showNotification('error', err.message);
    } finally {
      dispatch(setLoading({ key, value: false }));
    }
  };
}

export function addStudentToOrgBooking(values) {
  return async (dispatch, getState) => {
    let res;
    if (values.studentIds.length > 0) {
      const user = getCurrentUser(getState());
      values.bookedBy = user._id;
      const isWGT = values.type === LESSON_TYPES.WGT;

      const { supportedLanguage } = values.topicId ? await TopicService.get(values.topicId) : {};
      const booking = values.orgBookingId
        ? await OrganizationBookingService.get(values.orgBookingId)
        : null;

      try {
        const lessons = values.studentIds.map((studentId) => {
          const lessonPayload = {
            ...values,
            customerId: studentId,
          };

          if (supportedLanguage) lessonPayload.supportedLanguage = supportedLanguage;
          if (isWGT && booking?.questionSet) lessonPayload.questionSet = booking.questionSet;
          if (booking?.chooseTimeDeadline) {
            lessonPayload.chooseTimeDeadline = booking.chooseTimeDeadline;
          }

          return lessonPayload;
        });

        res = await LessonService.create(
          lessons,
          paramsForServer({
            action: 'create-by-meeopp.net',
          })
        );

        showNotification('success', 'Successfully booked');
        dispatch(fetchOrgBookingLessonById(values.orgBookingId));
      } catch (err) {
        showNotification('error', err.message);
      }
    }

    return res;
  };
}

export function updateOrgBooking(values) {
  return async () => {
    let res;
    if (values._id) {
      try {
        await OrganizationBookingService.patch(values._id, values);
        showNotification('success', 'Successfully updated');
      } catch (err) {
        showNotification('error', err.message);
        throw err;
      }
    }

    return res;
  };
}

// ==================== Organization Booking action ===== ---> END =========

export function fetchPreviewOrganizationDetails(orgId) {
  return async (dispatch) => {
    const loadingObj = {
      key: KEYS.fetchPreviewOrgDetails,
      value: true,
    };

    dispatch(setLoading(loadingObj));

    try {
      const data = await OrganizationService.get(orgId);
      dispatch(fetchPreviewOrganizationDetailsSuccess(data));
    } catch (e) {
      showNotification('error', 'Something went wrong!');
    }

    dispatch(setLoading(loadingObj));
    loadingObj.value = false;
    dispatch(setLoading(loadingObj));
  };
}

export function fetchPreviewUserDetails(userId) {
  return async (dispatch) => {
    const loadingObj = {
      key: KEYS.fetchPreviewUserDetails,
      value: true,
    };

    dispatch(setLoading(loadingObj));
    //
    try {
      const data = await UserService.get(userId);
      dispatch(fetchPreivewUserDetailsSuccess(data));
    } catch (e) {
      showNotification('error', 'Something went wrong!');
    }

    dispatch(setLoading(loadingObj));
    loadingObj.value = false;
    dispatch(setLoading(loadingObj));
  };
}

export function fetchOrganizationBookingOptions(organizationId) {
  return async (dispatch) => {
    if (!organizationId) return;
    const loadingObj = {
      key: KEYS.getOrganizationBookingOptions,
      value: true,
    };

    dispatch(setLoading(loadingObj));

    try {
      const res = await BookingOptionService.find(
        paramsForServer({
          query: { organizationIds: { $in: [organizationId] } },
          action: 'get-student-booking-options',
        })
      );

      dispatch(getOrganizationBookingOptions(res));
    } catch (err) {
      showNotification('error', err.message);
    } finally {
      loadingObj.value = false;
      dispatch(setLoading(loadingObj));
    }
  };
}

export function fetchOrgBookingOralScores(lessonsIds, options = {}) {
  return async (dispatch) => {
    const loadingObj = {
      key: KEYS.fetchOrgBookingOralScores,
      value: true,
    };

    dispatch(setLoading(loadingObj));
    //
    try {
      const otherParams = options.expand ? {} : { $select: ['averageScore'] };
      const { data } = await SessionAssessmentResultService.find(
        paramsForServer({
          query: {
            lessonId: { $in: lessonsIds },
            publishedAt: { $ne: null },
            ...otherParams,
          },
          populate: options.expand ? 'BASIC' : 'DEFAULT',
        })
      );

      dispatch(fetchLessonsOralScoresSuccess(data));
      return data;
    } catch (e) {
      console.error(e);
    } finally {
      loadingObj.value = false;
      dispatch(setLoading(loadingObj));
    }
  };
}

export function getOrgCurriculum(orgId) {
  return async (dispatch) => {
    const loadingObj = {
      key: KEYS.getOrgCurriculum,
      value: true,
    };

    dispatch(setLoading(loadingObj));
    //
    try {
      const data = await OrganizationService.get(orgId);
      return data;
    } catch (e) {
      console.error(e);
    } finally {
      loadingObj.value = false;
      dispatch(setLoading(loadingObj));
    }
  };
}
